import { Check, Search, ShieldCheck, UserPlus, UsersRound, X } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, Input, Spinner } from "../components/ui";
import { confirm } from "../lib/confirm";
import {
  useConnections,
  useRemoveConnection,
  useRequestConnection,
  useRespondConnection,
  useSearchUsers,
  useUserAccounts,
  type PublicPerson,
} from "../lib/hooks";
import { toast } from "../lib/toast";
import { WitnessPage } from "./Witness";

export function ConnectionsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "witness" ? "witness" : "people";
  const { data, isLoading } = useConnections();
  const [query, setQuery] = useState("");
  const { data: results = [] } = useSearchUsers(query);
  const request = useRequestConnection();
  const respond = useRespondConnection();
  const remove = useRemoveConnection();
  const [viewing, setViewing] = useState<string | null>(null);

  if (isLoading || !data) return <Spinner />;

  const sendRequest = async (username: string): Promise<void> => {
    const ok = await confirm({
      title: "Send connection request?",
      message: `We'll ask @${username} to connect with you.`,
      confirmLabel: "Send request",
    });
    if (!ok) return;
    try {
      await request.mutateAsync(username);
      toast.success("Request sent");
      setQuery("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send request");
    }
  };

  const answer = async (id: string, accept: boolean): Promise<void> => {
    try {
      await respond.mutateAsync({ id, accept });
      toast.success(accept ? "Connection accepted" : "Request declined");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const drop = async (id: string, name: string): Promise<void> => {
    const ok = await confirm({
      title: "Remove connection?",
      message: `You'll no longer be connected to ${name}.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync(id).then(() => toast.success("Removed"));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">People</h1>
        <p className="text-sm text-slate-500">Connections, shared financial relationships, and witness requests.</p>
      </div>
      <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
        <button onClick={() => setParams({ view: "people" }, { replace: true })} className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${view === "people" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`}><UsersRound size={17} /> Connections</button>
        <button onClick={() => setParams({ view: "witness" }, { replace: true })} className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${view === "witness" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`}><ShieldCheck size={17} /> Witness requests</button>
      </div>

      {view === "witness" ? <WitnessPage embedded /> : <>

      <Card>
        <h2 className="mb-2 font-semibold text-slate-700">Find people</h2>
        <div className="flex items-center gap-2">
          <Search size={18} className="text-slate-400" />
          <Input
            placeholder="Search by username"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1"
          />
        </div>
        {query.trim() && (
          <ul className="mt-3 divide-y divide-slate-100">
            {results.length === 0 && <li className="py-2 text-sm text-slate-400">No matches.</li>}
            {results.map((u) => (
              <li key={u.id} className="flex items-center justify-between py-2">
                <Person person={u} />
                <Button variant="ghost" onClick={() => void sendRequest(u.username ?? "")}>
                  <UserPlus size={16} /> Connect
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {data.incoming.length > 0 && (
        <Card>
          <h2 className="mb-2 font-semibold text-slate-700">Requests for you</h2>
          <ul className="divide-y divide-slate-100">
            {data.incoming.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2">
                <Person person={c.user} />
                <div className="flex gap-2">
                  <Button onClick={() => void answer(c.id, true)}>
                    <Check size={16} /> Accept
                  </Button>
                  <Button variant="ghost" onClick={() => void answer(c.id, false)}>
                    <X size={16} /> Decline
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <h2 className="mb-2 font-semibold text-slate-700">Your connections</h2>
        {data.connections.length === 0 ? (
          <p className="text-sm text-slate-500">No connections yet — search above to add some.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.connections.map((c) => (
              <li key={c.id} className="py-2">
                <div className="flex items-center justify-between">
                  <Person person={c.user} />
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setViewing(viewing === c.user.username ? null : c.user.username)} disabled={!c.user.username}>{viewing === c.user.username ? "Hide accounts" : "Accounts"}</Button>
                    <Link to={`/connections/${c.user.id}`} className="inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-blue-600 hover:bg-blue-50">View history</Link>
                    <Button variant="ghost" onClick={() => void drop(c.id, c.user.name)}>
                      Remove
                    </Button>
                  </div>
                </div>
                {viewing === c.user.username && c.user.username && (
                  <VisibleAccounts username={c.user.username} />
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {data.outgoing.length > 0 && (
        <Card>
          <h2 className="mb-2 font-semibold text-slate-700">Pending (sent)</h2>
          <ul className="divide-y divide-slate-100">
            {data.outgoing.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2">
                <Person person={c.user} />
                <Button variant="ghost" onClick={() => void drop(c.id, c.user.name)}>
                  Cancel
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      </>}
    </div>
  );
}

function Person({ person }: { person: PublicPerson }): JSX.Element {
  return (
    <div>
      <p className="text-sm font-medium text-slate-700">{person.name}</p>
      {person.username && <p className="text-xs text-slate-400">@{person.username}</p>}
    </div>
  );
}

function VisibleAccounts({ username }: { username: string }): JSX.Element {
  const { data, isLoading } = useUserAccounts(username);
  if (isLoading) return <p className="mt-2 text-xs text-slate-400">Loading…</p>;
  if (!data || data.accounts.length === 0) {
    return <p className="mt-2 text-xs text-slate-400">No accounts shared with you.</p>;
  }
  return (
    <ul className="mt-2 rounded-lg bg-slate-50 p-2 text-sm">
      {data.accounts.map((a) => (
        <li key={a.id} className="flex justify-between py-1">
          <span className="text-slate-600">{a.name}</span>
          <span className="text-xs uppercase tracking-wide text-slate-400">{a.type}</span>
        </li>
      ))}
    </ul>
  );
}
